import { api } from '../client';

export interface Integration {
  _id: string;
  deployment_id: string;
  deployment_hash: string;
  name: string;
  description: string;
  type: 'tunnel' | 'api' | 'webhook' | 'monitoring';
  status: 'active' | 'inactive' | 'revoked' | 'pending';
  scopes: string[];
  allowed_ips: string[];
  auto_approve_ips: string[];
  client_ids: string[];
  rate_limit: {
    requests_per_minute: number;
    requests_per_hour: number;
  };
  usage: {
    total_requests: number;
    last_request: string | null;
    last_ip: string | null;
  };
  webhook_url?: string;
  expires_at?: string;
  created_by?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IntegrationCredentials {
  api_key: string;
  deployment_id: string;
  deployment_hash: string;
  webhook_secret?: string;
}

export interface IntegrationStats {
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  totalRequests: number;
}

export interface CreateIntegrationData {
  name: string;
  description?: string;
  type?: string;
  scopes?: string[];
  allowed_ips?: string[];
  webhook_url?: string;
  expires_at?: string;
}

export interface UpdateIntegrationData {
  name?: string;
  description?: string;
  status?: string;
  scopes?: string[];
  allowed_ips?: string[];
  webhook_url?: string;
  expires_at?: string;
}

export interface IntegrationClient {
  _id: string;
  client_id: string;
  client_name: string;
  hostname: string;
  local_ip: string;
  status: string;
  deployment_name?: string;
  last_seen: string;
}

export const integrationsApi = {
  list: (params?: { status?: string; type?: string }) =>
    api.get<{ success: boolean; integrations: Integration[] }>(
      '/api/integrations',
      params as Record<string, string | number>,
    ),

  get: (id: string) =>
    api.get<{ success: boolean; integration: Integration }>(`/api/integrations/${id}`),

  stats: () =>
    api.get<{ success: boolean; stats: IntegrationStats }>('/api/integrations/stats'),

  create: (data: CreateIntegrationData) =>
    api.post<{
      success: boolean;
      integration: Integration;
      credentials: IntegrationCredentials;
      warning: string;
    }>('/api/integrations', data),

  update: (id: string, data: UpdateIntegrationData) =>
    api.put<{ success: boolean; integration: Integration }>(`/api/integrations/${id}`, data),

  delete: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/api/integrations/${id}`),

  regenerateKey: (id: string) =>
    api.post<{ success: boolean; api_key: string; warning: string }>(
      `/api/integrations/${id}/regenerate-key`,
    ),

  revoke: (id: string) =>
    api.post<{ success: boolean; message: string }>(`/api/integrations/${id}/revoke`),

  getClients: (id: string) =>
    api.get<{ success: boolean; clients: IntegrationClient[] }>(
      '/api/integrations/external/clients',
      // The external clients endpoint uses the integration's client_ids
      // For admin, we use the integration detail which includes client_ids,
      // then fetch those clients. Using a dedicated admin endpoint instead.
    ),

  // Link a client to this integration (via api.js route)
  addClient: (integrationId: string, clientId: string) =>
    api.put<{ success: boolean; client: IntegrationClient }>(
      `/api/clients/${clientId}/integration`,
      { integration_id: integrationId },
    ),

  // Unlink a client from integration
  removeClient: (_integrationId: string, clientId: string) =>
    api.put<{ success: boolean; client: IntegrationClient }>(
      `/api/clients/${clientId}/integration`,
      { integration_id: null },
    ),

  // Webhook management
  updateWebhook: (id: string, data: { webhook_url?: string; regenerate_secret?: boolean }) =>
    api.put<{ success: boolean; webhook_url: string; webhook_secret?: string }>(
      `/api/integrations/${id}/webhook`,
      data,
    ),

  testWebhook: (id: string) =>
    api.post<{ success: boolean; statusCode?: number; error?: string }>(
      `/api/integrations/${id}/webhook/test`,
    ),
};
